import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { usePickableProjects } from '../../hooks/useExternalUsers';

/**
 * Controlled multi-select for assigning external users to projects.
 *
 * Sources options from `usePickableProjects()` which calls the backend's
 * `/api/admin/external-users/pickable-projects` endpoint — already server-filtered
 * to `state = 'in_progress' AND deletedAt IS NULL`. Do NOT add a
 * client-side filter (T-15-03-07: keeps the predicate in one place).
 *
 * UX matches the app's standard combobox pattern (see AssignmentForm role-name picker
 * and LeadFormModal customer combobox): chips for selected projects above a typeahead
 * input that opens a filtered dropdown on focus.
 */

export interface ProjectPickerProps {
  value: number[];
  onChange: (next: number[]) => void;
  error?: string;
  disabled?: boolean;
}

export function ProjectPicker({ value, onChange, error, disabled }: ProjectPickerProps) {
  const { data: projects, isLoading, isError } = usePickableProjects();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const allProjects = useMemo(() => projects ?? [], [projects]);

  const selected = useMemo(
    () => allProjects.filter((p) => value.includes(p.id)),
    [allProjects, value],
  );

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allProjects
      .filter((p) => !value.includes(p.id))
      .filter((p) => (q ? p.name.toLowerCase().includes(q) : true));
  }, [allProjects, value, query]);

  const add = (id: number) => {
    if (!value.includes(id)) {
      onChange([...value, id]);
    }
    setQuery('');
    inputRef.current?.focus();
  };

  const remove = (id: number) => {
    onChange(value.filter((v) => v !== id));
  };

  if (isLoading) {
    return <p className="text-sm text-gray-500">Loading projects…</p>;
  }
  if (isError) {
    return <p className="text-sm text-red-600">Failed to load projects.</p>;
  }

  return (
    <div
      ref={containerRef}
      role="group"
      aria-label="Project selection"
      aria-invalid={error ? 'true' : 'false'}
    >
      <label
        htmlFor="project-picker-search"
        className="block text-sm font-medium text-gray-700"
      >
        Projects
      </label>

      {selected.length > 0 && (
        <ul className="mt-1 flex flex-wrap gap-1.5">
          {selected.map((p) => (
            <li
              key={p.id}
              className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700 border border-indigo-200"
            >
              <span>{p.name}</span>
              <button
                type="button"
                onClick={() => remove(p.id)}
                disabled={disabled}
                aria-label={`Remove ${p.name}`}
                className="text-indigo-500 hover:text-indigo-700 focus:outline-none focus:ring-1 focus:ring-indigo-500 rounded"
              >
                <XMarkIcon className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="relative mt-1">
        <input
          ref={inputRef}
          id="project-picker-search"
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder="Search projects…"
          disabled={disabled}
          autoComplete="off"
          className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        />
        <button
          type="button"
          onClick={() => {
            setIsOpen((open) => !open);
            inputRef.current?.focus();
          }}
          disabled={disabled}
          aria-label={isOpen ? 'Close project list' : 'Open project list'}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600"
        >
          <ChevronDownIcon
            className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
        </button>

        {isOpen && (
          <div
            role="listbox"
            className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-64 overflow-y-auto"
          >
            {allProjects.length === 0 ? (
              <p className="px-3 py-2 text-sm text-gray-500">No projects available.</p>
            ) : suggestions.length === 0 ? (
              <p className="px-3 py-2 text-sm text-gray-500">No matching projects.</p>
            ) : (
              suggestions.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  role="option"
                  aria-selected="false"
                  onClick={() => add(p.id)}
                  disabled={disabled}
                  className="w-full text-left px-3 py-2 text-sm text-gray-900 hover:bg-indigo-50 focus:outline-none focus:bg-indigo-50"
                >
                  {p.name}
                </button>
              ))
            )}
          </div>
        )}
      </div>

      <p className="mt-2 text-sm text-gray-600">Selected: {value.length}</p>

      {error && (
        <p role="alert" className="mt-1 text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}
